// Shared presentation helpers for the AiFeed feature.
// No JSX — safe to import from any card component without circular dependencies.

import { Hospital, Sparkles } from "lucide-react";
import type { ElementType } from "react";
import type { AiFeedItem } from "./network/types";
import {
  AiFeedItemProducer,
  AiFeedItemType,
  isCreateConditionItem,
} from "./network/types";

// ---------------------------------------------------------------------------
// Producer labels
// ---------------------------------------------------------------------------

const PRODUCER_LABELS: Record<AiFeedItemProducer, string> = {
  [AiFeedItemProducer.avCallExtraction]: "AV Call",
  [AiFeedItemProducer.amazonCiqExtraction]: "Intake (CIQ)",
};

export function getProducerLabel(producer: AiFeedItemProducer): string {
  return PRODUCER_LABELS[producer] ?? producer;
}

// ---------------------------------------------------------------------------
// Provenance text — extensible intent-type map
// ---------------------------------------------------------------------------

const PROVENANCE_BUILDERS: Partial<
  Record<AiFeedItemType, (item: AiFeedItem) => string>
> = {
  [AiFeedItemType.createCondition]: (item) => {
    if (!isCreateConditionItem(item)) return "";
    return `${item.intent.extractedTerm} mentioned during ${getProducerLabel(item.producer)}`;
  },
  // [AiFeedItemType.createMed]: (item) => ...
};

/**
 * Returns a human-readable provenance string for the given item, or null if
 * the item's type doesn't have a builder registered yet.
 */
export function getProvenanceText(item: AiFeedItem): string | null {
  const builder = PROVENANCE_BUILDERS[item.itemType];
  return builder ? builder(item) : null;
}

// ---------------------------------------------------------------------------
// Intent icon — one source for all variants
// ---------------------------------------------------------------------------

// Maps each item type to the Lucide icon that represents its charting destination.
// Add one line here when a new AiFeedItemType ships.
const ITEM_TYPE_ICON: Partial<Record<AiFeedItemType, ElementType>> = {
  [AiFeedItemType.createCondition]: Hospital,
  // [AiFeedItemType.createMed]: Pill,  // when create_med ships
};

export function getIntentIcon(itemType: AiFeedItemType): ElementType {
  return ITEM_TYPE_ICON[itemType] ?? Sparkles;
}

// ---------------------------------------------------------------------------
// Intent labels — physician-friendly labels by item type
// ---------------------------------------------------------------------------

const INTENT_LABELS: Partial<Record<AiFeedItemType, string>> = {
  [AiFeedItemType.createCondition]: "New Medical Condition",
  // [AiFeedItemType.createMed]: "New Medication",
};

export function getIntentLabel(itemType: AiFeedItemType): string {
  return INTENT_LABELS[itemType] ?? itemType;
}

// ---------------------------------------------------------------------------
// Charted-to labels — where accepted items land
// ---------------------------------------------------------------------------

const CHARTED_TO_LABELS: Partial<Record<AiFeedItemType, string>> = {
  [AiFeedItemType.createCondition]: "Medical History",
  // [AiFeedItemType.createMed]: "Medications",
};

export function getChartedToLabel(itemType: AiFeedItemType): string {
  return CHARTED_TO_LABELS[itemType] ?? "Chart";
}
